import React, { useState, useEffect, useContext } from 'react'
import { complement, compose, isEmpty } from 'ramda'
import format from 'date-fns/format'
import parse from 'date-fns/parse'
import add from 'date-fns/add'
import styled from 'styled-components'
import { Box, Card, Flex, Text, Button } from 'pcln-design-system'
import { ArrowRight, Icon } from 'pcln-icons'
import { SetiRedesignContext } from './providers/RedesignProvider'

import {
  HOTEL_SEARCH_DATA,
  PACKAGE_SEARCH_DATA,
  getPersistedSearchDataByKeys,
  SavedSearch
} from '../utils/local-forage-helper'
import capitalizeLetter from '../utils/capitalize-letter'
import { handleSubmitForHotels } from './Hotels/SearchForm/utils'
import {
  HOTEL_PRODUCT_TYPE,
  RENTAL_CAR_PRODUCT_TYPE,
  VACATION_PACKAGE_PRODUCT_TYPE
} from '../constants/products'

import analytics from '../utils/analytics'
import { ANALYTICS_CATEGORY_HOME } from '../constants/analytics'
import { LOCATION_SEARCH_TYPE } from '../types'

const DATE_FORMAT = 'E, MMM dd'

function getDisplayName(location: LOCATION_SEARCH_TYPE | null) {
  if (!location) {
    return ''
  }

  const { cityName, stateCode, countryCode, itemName } = location

  const displayCode = stateCode && stateCode !== '--' ? stateCode : countryCode

  return cityName && displayCode ? `${cityName}, ${displayCode}` : itemName
}

const filterFalsey = (primary: string, fallback: string) => (item: any) => {
  const locations = [item[primary], item[fallback], item.locationObject]
    .filter(Boolean)
    .filter(complement(isEmpty))

  return locations?.[0]
}

// we need endLocation & startLocation as a fallback because
// next-landing uses these keys to save package searches
// @todo remove the fallbacks after d-home is deprecated for some time
const getStartLocation = filterFalsey('startLocationObject', 'startLocation')
const getEndLocation = filterFalsey('endLocationObject', 'endLocation')

const getProductType = (item: any) => {
  const { tripType } = item

  switch (tripType) {
    case 'STAY':
      return HOTEL_PRODUCT_TYPE
    case 'AH':
      return RENTAL_CAR_PRODUCT_TYPE
    default:
      return VACATION_PACKAGE_PRODUCT_TYPE
  }
}

function renderIcon(start: string, end: string) {
  if (start && end && start !== end) {
    return <ArrowRight fontWeight="bold" size="12" />
  }
}

function formatDates({ startDate = '', endDate = '' }) {
  if (!startDate) {
    const today = new Date()
    const tomorrow = add(today, { days: 1 })

    return [format(today, DATE_FORMAT), format(tomorrow, DATE_FORMAT)]
  }

  return [
    format(parse(startDate, 'yyyyMMdd', new Date()), DATE_FORMAT),
    format(parse(endDate, 'yyyyMMdd', new Date()), DATE_FORMAT)
  ]
}

const Wrapper = styled.div<{ isHPCornerShadowColorRedesign: boolean }>`
  background-color: ${props =>
    props.isHPCornerShadowColorRedesign
      ? props.theme.colors.lightestBackground
      : props.theme.colors.lightBackground};

  margin-bottom: -16px;
  padding: 16px 0;
`

const StyledFlex = styled(Flex)`
  flex-wrap: wrap;
  margin-left: auto;
  margin-right: auto;
  max-width: 1280px;
  padding-left: 28px;
`

export default function RecentSearchCards() {
  const [recentSearches, setRecentSearches] = useState<SavedSearch[]>([])

  useEffect(() => {
    getPersistedSearchDataByKeys(
      [HOTEL_SEARCH_DATA, PACKAGE_SEARCH_DATA],
      true
    ).then(data => {
      if (data) {
        setRecentSearches(data.slice(0, 2))
      }
    })
  }, [])
  const { isHPTextVariant } = useContext(SetiRedesignContext)

  const recentSearchCards = recentSearches.map((item: any, index: number) => {
    const product = getProductType(item)
    const startDisplayName = compose(getDisplayName, getStartLocation)(item)
    const endDisplayName = compose(getDisplayName, getEndLocation)(item)

    const [startDate, endDate] = formatDates(item)
    const iconName =
      product === VACATION_PACKAGE_PRODUCT_TYPE
        ? 'Luggage'
        : capitalizeLetter(product)

    const arrow = renderIcon(startDisplayName, endDisplayName)

    return (
      <Card
        color="background.lightest"
        boxShadowSize="md"
        borderRadius="xl"
        mr={index === 0 ? '36px' : 0}
        px="10px"
        py={2}
        height="74px"
        width="388px"
        style={{
          cursor: 'pointer'
        }}
        key={item.timestamp || index}
        onClick={() => {
          handleSubmitForHotels(item)

          analytics.fireEvent({
            category: ANALYTICS_CATEGORY_HOME,
            action: 'Personalized Shortcut',
            label: 'Recent Search Shortcut'
          })
        }}
      >
        <Flex>
          <Icon color="primary.dark" name={iconName} size="20" mr="10px" />
          <Box
            color="#001833"
            style={{ flexGrow: '1' }}
            id={`buttonDescription${item.timestamp || index}`}
          >
            <Text
              fontSize={1}
              fontWeight="bold"
              textStyle={isHPTextVariant ? 'heading6' : ''}
              mb={1}
            >
              {startDisplayName} {arrow}{' '}
              {startDisplayName !== endDisplayName ? endDisplayName : ''}
            </Text>
            <Text
              fontSize={0}
              fontWeight="medium"
              textStyle={isHPTextVariant ? 'caption' : ''}
            >
              {startDate} - {endDate}
            </Text>
          </Box>
          <Button
            variation="link"
            size="small"
            mt="3"
            aria-describedby={`buttonDescription${item.timestamp || index}`}
          >
            Search Again
          </Button>
        </Flex>
      </Card>
    )
  })
  const { isHPCornerShadowColorRedesign } = useContext(SetiRedesignContext)
  return recentSearchCards.length ? (
    <Wrapper isHPCornerShadowColorRedesign={isHPCornerShadowColorRedesign}>
      <StyledFlex>
        <Text
          width="100%"
          mb={2}
          fontSize={2}
          fontWeight="bold"
          textStyle="heading5"
        >
          Your Recent Searches
        </Text>
        {recentSearchCards}
      </StyledFlex>
    </Wrapper>
  ) : null
}
